<template>
  <v-container fluid id="transferSelection" class="content-container pa-0" width="800">
    <v-row class="m-0">
      <v-col cols="12" class="pa-0">
        <!-- If images exist, show carousel -->
        <div v-if="listing" class="listing-main-content">
          <!-- Improved skeleton with fixed height -->
          <v-skeleton-loader v-if="isLoading" type="image, article, actions" class="ma-2 skeleton-loader"
            height="400"></v-skeleton-loader>

          <template v-else>
            <!-- Add aspect ratio container for carousel to prevent layout shift -->
            <div class="carousel-container">
              <DSCarousel v-if="listing.images && listing.images.length > 0" :images="listing.images"
                :title="listing.title" :show-captions="true" class="listing-carousel mb-4" />

              <!-- Display error if images are missing -->
              <v-alert v-else type="warning" variant="outlined" class="mx-2 mb-3">
                No images available for this listing
              </v-alert>
            </div>

            <!-- Main content -->
            <div class="listing-section mb-4 px-3">
              <!-- Header with title and info -->
              <ListingHeader :title="listing.title" :base-price="rateTable[0].price" :currency="currency"
                :locale="locale" :listing-type="listing.listing_type" :is-best-seller="listing.isBestSeller"
                :chips="listing.chips" :review-count="listing.reviewCount" :schedules="listing.content.sched"
                :duration="listing.content.duration" />

              <!-- Add toast notification -->
              <v-snackbar v-model="showToast" :timeout="3000" color="success" location="top">
                {{ toastMessage }}
                <template v-slot:actions>
                  <v-btn variant="text" @click="showToast = false">Close</v-btn>
                </template>
              </v-snackbar>

              <!-- Main description -->
              <div class="my-3">
                <ListingDescription :seo-description="seo_description" :origin="listing.origin"
                  :destination="listing.destination" :listing-type="listing.listing_type" />
              </div>

              <!-- Add share section before FAQ -->
              <div class="my-4">
                <div class="share-section">
                  <h2 class="text-h5 mb-3 font-weight-bold d-flex align-center">
                    <v-icon size="x-large" color="primary" class="mr-2">mdi-share-variant</v-icon>
                    Share This Listing
                  </h2>
                  <div class="share-buttons">
                    <v-btn variant="outlined" rounded color="#4267B2" class="share-btn"
                      @click="shareToSocial('facebook')">
                      <v-icon :size="isMobileOnly ? 'default' : 'x-large'" color="#4267B2">mdi-facebook</v-icon>
                      <span class="ml-2 d-none d-sm-inline">Facebook</span>
                    </v-btn>
                    <v-btn variant="outlined" rounded color="#1DA1F2" class="share-btn"
                      @click="shareToSocial('twitter')">
                      <v-icon :size="isMobileOnly ? 'default' : 'x-large'" color="#1DA1F2">mdi-twitter</v-icon>
                      <span class="ml-2 d-none d-sm-inline">Twitter</span>
                    </v-btn>
                    <v-btn variant="outlined" rounded color="#25D366" class="share-btn"
                      @click="shareToSocial('whatsapp')">
                      <v-icon :size="isMobileOnly ? 'default' : 'x-large'" color="#25D366">mdi-whatsapp</v-icon>
                      <span class="ml-2 d-none d-sm-inline">WhatsApp</span>
                    </v-btn>
                    <v-btn variant="outlined" rounded :color="hasCopied ? 'success' : 'grey'" class="share-btn"
                      @click="copyToClipboard">
                      <v-icon :size="isMobileOnly ? 'default' : 'x-large'"
                        :color="hasCopied ? 'success' : 'grey'">mdi-content-copy</v-icon>
                      <span class="ml-2 d-none d-sm-inline">{{ hasCopied ? 'Copied!' : 'Copy Link' }}</span>
                    </v-btn>
                  </div>
                </div>
              </div>

              <!-- FAQ Section with accordion -->
              <ListingFAQ :service-provider-name="listing.serviceProvider.name" :duration="listing.content.duration"
                :listing-type="listing.listing_type" :base-rate="ratePlans[currency]?.base" :currency="currency"
                :locale="locale" :schedules="listing.content.sched" />

              <!-- Reviews Section -->
              <div class="my-5" v-if="reviews && reviews.length">
                <h2 class="text-h5 mb-4 font-weight-bold d-flex align-center">
                  <v-icon size="x-large" color="primary" class="mr-2">mdi-star</v-icon>
                  Customer Reviews
                </h2>

                <!-- Desktop layout with grid -->
                <v-row v-if="!isMobileOnly" class="reviews-grid">
                  <v-col v-for="(review, index) in reviews" :key="index" cols="12" md="4" class="mb-3">
                    <v-card height="100%" class="d-flex flex-column review-card" @click="openReviewDialog(index)">
                      <v-card-text class="flex-grow-1">
                        <div class="mb-2">
                          <v-rating :model-value="review.rating" color="amber" half-increments readonly size="small"
                            density="compact"></v-rating>
                        </div>
                        <h4 class="text-subtitle-1 font-weight-bold mb-2">{{ review.title }}</h4>
                        <p class="text-body-2 review-text mb-3">"{{ review.text }}"</p>
                        <div class="d-flex align-center">
                          <v-avatar size="40" class="mr-3">
                            <v-img :src="review.avatar" :alt="`${review.name}'s avatar`"></v-img>
                          </v-avatar>
                          <div>
                            <p class="text-subtitle-2 mb-0">{{ review.name }}</p>
                            <p class="text-caption text-primary d-flex align-center">
                              <span v-if="review.countryFlag" class="mr-1">{{ review.countryFlag }}</span>
                              {{ review.location }}
                            </p>
                          </div>
                        </div>
                      </v-card-text>
                    </v-card>
                  </v-col>
                </v-row>

                <!-- Mobile layout with horizontal scroll -->
                <div v-else class="reviews-scroll-container">
                  <div class="reviews-scroll-inner">
                    <div class="reviews-nav-button reviews-nav-prev" @click="scrollReviews('prev')">
                      <v-icon>mdi-chevron-left</v-icon>
                    </div>

                    <div ref="reviewsScrollWrapper" class="reviews-scroll-wrapper">
                      <v-card v-for="(review, index) in reviews" :key="index" width="280"
                        class="review-card review-card-scroll mr-3" @click="openReviewDialog(index)">
                        <v-card-text>
                          <div class="mb-2">
                            <v-rating :model-value="review.rating" color="amber" half-increments readonly size="small"
                              density="compact"></v-rating>
                          </div>
                          <h4 class="text-subtitle-1 font-weight-bold mb-2">{{ review.title }}</h4>
                          <p class="text-body-2 review-text mb-3">"{{ review.text }}"</p>
                          <div class="d-flex align-center">
                            <v-avatar size="40" class="mr-3">
                              <v-img :src="review.avatar" :alt="`${review.name}'s avatar`"></v-img>
                            </v-avatar>
                            <div>
                              <p class="text-subtitle-2 mb-0">{{ review.name }}</p>
                              <p class="text-caption text-primary d-flex align-center">
                                <span v-if="review.countryFlag" class="mr-1">{{ review.countryFlag }}</span>
                                {{ review.location }}
                              </p>
                            </div>
                          </div>
                        </v-card-text>
                      </v-card>

                      <!-- Indicator that there are more reviews to scroll -->
                      <div class="scroll-indicator d-flex align-center justify-center pa-4">
                        <v-icon color="grey-lighten-1" size="large">mdi-gesture-swipe-horizontal</v-icon>
                        <span class="ml-2 text-caption text-grey-lighten-1">Swipe for more</span>
                      </div>
                    </div>

                    <div class="reviews-nav-button reviews-nav-next" @click="scrollReviews('next')">
                      <v-icon>mdi-chevron-right</v-icon>
                    </div>
                  </div>

                  <!-- Scroll position indicators -->
                  <div class="reviews-indicators">
                    <div v-for="(_, index) in Math.ceil(reviews.length / 2)" :key="index" class="reviews-indicator-dot"
                      :class="{ 'active': currentScrollIndex === index }" @click="scrollToIndex(index)"></div>
                  </div>
                </div>

                <!-- Review Dialog -->
                <v-dialog v-model="reviewDialog" max-width="600" transition="dialog-bottom-transition"
                  @keydown.left="showPreviousReview" @keydown.right="showNextReview">
                  <v-card v-if="currentReview" class="review-dialog-card">
                    <v-card-title class="d-flex justify-space-between align-center pt-4 px-4">
                      <span class="text-h6">{{ currentReview.title }}</span>
                      <v-btn icon="mdi-close" variant="text" density="comfortable"
                        @click="reviewDialog = false"></v-btn>
                    </v-card-title>

                    <v-card-text class="px-4 pt-2">
                      <div class="mb-3 d-flex align-center">
                        <v-rating :model-value="currentReview.rating" color="amber" half-increments readonly
                          size="small"></v-rating>
                        <span class="ml-2 text-caption">{{ formatReviewDate() }}</span>
                      </div>

                      <v-fade-transition mode="out-in">
                        <div :key="currentReviewIndex" class="mb-5 review-content-wrapper" v-touch="{
                          left: showNextReview,
                          right: showPreviousReview
                        }">
                          <p class="text-body-1 review-dialog-text">"{{ currentReview.text }}"</p>
                        </div>
                      </v-fade-transition>

                      <div class="d-flex align-center mt-4">
                        <v-avatar size="50" class="mr-3">
                          <v-img :src="currentReview.avatar" :alt="`${currentReview.name}'s avatar`"></v-img>
                        </v-avatar>
                        <div>
                          <p class="text-subtitle-1 font-weight-bold mb-0">{{ currentReview.name }}</p>
                          <p class="text-body-2 d-flex align-center">
                            <span v-if="currentReview.countryFlag" class="mr-1">{{ currentReview.countryFlag }}</span>
                            {{ currentReview.location }}
                          </p>
                        </div>
                      </div>
                    </v-card-text>

                    <v-divider class="mt-4"></v-divider>

                    <v-card-actions class="review-navigation">
                      <v-btn variant="outlined" prepend-icon="mdi-chevron-left" @click="showPreviousReview"
                        :disabled="currentReviewIndex === 0" class="review-nav-btn" rounded>
                        <span class="d-none d-sm-inline">Previous</span>
                      </v-btn>

                      <span class="text-caption">{{ currentReviewIndex + 1 }} of {{ reviews.length }}</span>

                      <v-btn variant="outlined" append-icon="mdi-chevron-right" @click="showNextReview"
                        :disabled="currentReviewIndex === reviews.length - 1" class="review-nav-btn" rounded>
                        <span class="d-none d-sm-inline">Next</span>
                      </v-btn>
                    </v-card-actions>

                    <div class="keyboard-instructions text-caption text-center mb-2 d-none d-sm-block">
                      Press <kbd>←</kbd> / <kbd>→</kbd> to navigate
                    </div>
                  </v-card>
                </v-dialog>
              </div>
            </div>
          </template>
        </div>

        <!-- Error display if listing data is missing -->
        <v-alert v-else type="error" variant="tonal" class="mx-2 mb-3">
          <template v-slot:prepend>
            <v-icon icon="mdi-alert-circle" size="large"></v-icon>
          </template>
          <div>
            <h3 class="text-h6">Unable to load listing data</h3>
            <p>Please try refreshing the page. If the problem persists, contact support.</p>
            <v-btn color="error" variant="text" prepend-icon="mdi-refresh" @click="window.location.reload()"
              class="mt-2">
              Refresh Page
            </v-btn>
          </div>
        </v-alert>
      </v-col>

      <!-- Rest of the components with conditional rendering for mobile -->
      <template v-if="listing">
        <!-- Mobile view now shows all content without hiding details -->
        <v-col v-if="isMobileOnly" cols="12" class="pa-0">
          <div class="listing-section px-3 pb-2">
            <!-- Rates section if not a shared transfer -->
            <div v-if="listing.listing_type !== 'SHARED_TRANSFER'" class="mb-4">
              <h2 class="text-h5 mb-3 font-weight-bold">Pricing</h2>
              <ListingRates :rate-table="rateTable" :currency="currency" :locale="locale" />
            </div>

            <!-- Inclusions and Exclusions -->
            <div class="mb-4">
              <h2 class="text-h5 mb-3 font-weight-bold">What's Included</h2>
              <ListingInclusions :inclusions="listing.content.markdown_content.inclusions"
                :exclusions="listing.content.markdown_content.exclusions" />
            </div>

            <!-- Pickup and Drop off Locations -->
            <div class="mb-4">
              <h2 class="text-h5 mb-3 font-weight-bold">Tour Locations</h2>
              <ListingLocations :pickup-locations="listing.content.pickup" :dropoff-locations="listing.content.dropoff"
                :origin-title="getDestinationTitle(listing.origin)"
                :destination-title="getDestinationTitle(listing.destination)" />
            </div>

            <!-- Itinerary Timeline -->
            <div class="mb-4">
              <itinerary-timeline :data="processItineraryData(listing.content.markdown_content.itinerary)"
                :images="listing.images" />
            </div>
          </div>
        </v-col>

        <!-- Desktop view shows all sections normally -->
        <template v-else>
          <v-col cols="12" class="pa-0">
            <div class="listing-section mb-4 px-3">
              <!-- Rates section if not a shared transfer -->
              <div v-if="listing.listing_type !== 'SHARED_TRANSFER'" class="mb-5">
                <h2 class="text-h5 mb-4 font-weight-bold">Pricing</h2>
                <ListingRates :rate-table="rateTable" :currency="currency" :locale="locale" />
              </div>

              <!-- Inclusions and Exclusions -->
              <div class="mb-5">
                <h2 class="text-h5 mb-4 font-weight-bold">What's Included</h2>
                <ListingInclusions :inclusions="listing.content.markdown_content.inclusions"
                  :exclusions="listing.content.markdown_content.exclusions" />
              </div>

              <!-- Pickup and Drop off Locations -->
              <div class="mb-5">
                <h2 class="text-h5 mb-4 font-weight-bold">Tour Locations</h2>
                <ListingLocations :pickup-locations="listing.content.pickup"
                  :dropoff-locations="listing.content.dropoff" :origin-title="getDestinationTitle(listing.origin)"
                  :destination-title="getDestinationTitle(listing.destination)" />
              </div>
            </div>
          </v-col>

          <!-- Itinerary Timeline -->
          <v-col cols="12" class="pa-0">
            <div class="listing-section mb-4 px-3">
              <itinerary-timeline :data="processItineraryData(listing.content.markdown_content.itinerary)"
                :images="listing.images" />
            </div>
          </v-col>
        </template>

        <!-- Featured Highlights -->
        <v-col v-if="listing.featured_highlights && listing.featured_highlights.length" cols="12" class="pa-0">
          <div class="listing-section mb-4 px-3">
            <h2 class="text-h5 mb-3 font-weight-bold">Highlights</h2>
            <v-list dense>
              <v-list-item v-for="highlight in listing.featured_highlights" :key="highlight"
                prepend-icon="mdi-check-circle" class="px-0 py-1">
                <v-list-item-title>{{ highlight }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </div>
        </v-col>

        <!-- Meta Keywords -->
        <v-col v-if="listing.meta_keywords && listing.meta_keywords.length" cols="12" class="pa-0">
          <div class="listing-section mb-4 px-3">
            <h2 class="text-h5 mb-3 font-weight-bold">Keywords</h2>
            <v-chip-group>
              <v-chip v-for="keyword in listing.meta_keywords" :key="keyword" variant="outlined" color="primary"
                size="small" class="mr-1 mb-1">
                {{ keyword }}
              </v-chip>
            </v-chip-group>
          </div>
        </v-col>

        <!-- Alerts section -->
        <v-col v-if="listing.content.markdown_content.importantNotes" cols="12" class="pa-0">
          <div class="listing-section mb-4 px-3">
            <ListingAlerts :title="listing.title" :important-notes="listing.content.markdown_content.importantNotes" />
          </div>
        </v-col>

        <!-- Suggestions with improved placeholder to reduce CLS -->
        <v-col cols="12" class="pa-0">
          <div class="listing-section mb-4 px-3">
            <h2 class="text-h5 mb-3 font-weight-bold">Similar Tours</h2>

            <Suspense>
              <ListingSuggestions :items="suggestions" />
              <template #fallback>
                <div class="suggestions-placeholder">
                  <v-skeleton-loader type="card, card, card" class="suggestions-skeleton"></v-skeleton-loader>
                </div>
              </template>
            </Suspense>
          </div>
        </v-col>
      </template>
    </v-row>

    <!-- Pre-allocate space for footer to prevent layout shift -->
    <div class="footer-spacer"></div>

    <!-- Sticky booking footer -->
    <div class="footer-wrapper">
      <ListingFooter v-if="listing" :price="rateTable[0].price" :currency="currency" :locale="locale" :listing="listing"
        :show-from-label="!isRateTheSame" :is-mobile="isMobile" class="listing-footer" />
    </div>
  </v-container>
</template>

<script setup>
import { computed, ref, watch, onErrorCaptured, onMounted, onUnmounted } from 'vue';
import { useDisplay } from 'vuetify';
import { Destinations } from '~/data/Records';
import { formatCurrency } from '~/utils/index';
import { calculatePricing } from '~/utils/objectHelpers';
import { storeToRefs } from 'pinia';
import { useListingsStore } from '~/stores/listingStore';
import { useCurrencyStore } from '~/stores/currencyStore';

// Import new components
import ListingHeader from '~/components/listing/ListingHeader.vue';
import ListingDescription from '~/components/listing/ListingDescription.vue';
import ListingRates from '~/components/listing/ListingRates.vue';
import ListingFAQ from '~/components/listing/ListingFAQ.vue';
import ListingInclusions from '~/components/listing/ListingInclusions.vue';
import ListingLocations from '~/components/listing/ListingLocations.vue';
import ListingAlerts from '~/components/listing/ListingAlerts.vue';
import ListingSuggestions from '~/components/listing/ListingSuggestions.vue';
import ListingFooter from '~/components/listing/ListingFooter.vue';

// Store and state management
const listingStore = useListingsStore();
const store = useCurrencyStore();
const { locale, currency } = storeToRefs(store);
const { countries } = storeToRefs(listingStore);
const mobile = useDisplay().smAndDown;
const isMobile = ref(mobile.value);

// Loading state - reduced time to minimize CLS
const isLoading = ref(true);
setTimeout(() => {
  isLoading.value = false;
}, 500); // Reduced from 800ms to 500ms for better performance

// Error handling
const hasError = ref(false);
const errorMessage = ref('');

// State for share functionality
const showToast = ref(false);
const toastMessage = ref('');
const hasCopied = ref(false);
const route = useRoute();

// Initialize component sizes early to reduce CLS
const footerHeight = ref('70px');
const carouselHeight = ref('400px');

// Reviews data
const reviews = ref([]);
const reviewDialog = ref(false);
const currentReviewIndex = ref(0);
const currentReview = computed(() => reviews.value[currentReviewIndex.value]);

// Add refs for scroll functionality
const reviewsScrollWrapper = ref(null);
const currentScrollIndex = ref(0);

// Props
const props = defineProps({
  listing: {
    required: true,
    type: Object,
  },
});

// Extract necessary data from props
const {
  content: { seo_description = '' } = {},
  code = '',
  origin = '',
  destination = '',
  ratePlans = {},
} = props.listing || {};

// Compute rate table and check if all rates are the same
const rateTable = ref([]);
const isRateTheSame = ref(true);

// Immediately initialize rate table with default values to prevent CLS
rateTable.value = [{ person: 1, price: 0 }];

// Get suggestions for related listings
const suggestions = computed(() => {
  try {
    return listingStore.getSuggestions(code, origin, destination);
  } catch (error) {
    console.error('Error fetching suggestions:', error);
    return [];
  }
});

// Initialize rateTable
watch(() => [props.listing, currency.value], () => {
  try {
    if (props.listing && props.listing.ratePlans && props.listing.ratePlans[currency.value]) {
      rateTable.value = calculatePricing(
        props.listing.ratePlans[currency.value],
        code.includes('PrivBoat')
      );
      isRateTheSame.value = rateTable.value.every(
        (value) => value.price === rateTable.value[0].price
      );
    } else {
      // Fallback if rate plans are not available
      rateTable.value = [{ person: 1, price: 0 }];
      isRateTheSame.value = true;
    }
  } catch (error) {
    console.error('Error calculating rates:', error);
    hasError.value = true;
    errorMessage.value = 'Unable to calculate rates';
  }
}, { immediate: true });

// Get destination title from destination code
const getDestinationTitle = (code) => {
  const destination = Destinations.find((i) => i.code === code);
  return destination ? destination.title : code;
};

// Process itinerary data to ensure correct format for the timeline component
const processItineraryData = (itineraryData) => {
  // If itinerary data is empty or undefined, return empty array
  if (!itineraryData || Object.keys(itineraryData).length === 0) {
    return [];
  }

  // Convert object-based itinerary to array format expected by timeline component
  return Object.entries(itineraryData).map(([title, description]) => ({
    title,
    description
  }));
};

// Error handling for child components
onErrorCaptured((error) => {
  console.error('Captured an error:', error);
  hasError.value = true;
  errorMessage.value = 'An error occurred while rendering the page. Please try again.';
  return false; // Prevent error from propagating
});

// Update layout dimensions based on screen size to prevent CLS
const updateLayoutDimensions = () => {
  const width = window.innerWidth;

  // Set footer height based on screen size
  footerHeight.value = width < 600 ? '80px' : '70px';

  // Set carousel height based on screen size
  carouselHeight.value = width < 600 ? '30vh' : '400px';

  // Update mobile status
  isMobileOnly.value = width < 600;
};

// Initialize reviews from listing data
const initializeReviews = () => {
  // Check if listing has reviews
  if (props.listing && props.listing.reviews && props.listing.reviews.length > 0) {
    // Get keywords from listing data to use for relevance filtering
    const title = props.listing.title.toLowerCase();
    const description = props.listing.content.seo_description.toLowerCase();

    // Extract keywords from itinerary if available
    const itineraryKeywords = [];
    if (props.listing.content.markdown_content.itinerary) {
      Object.entries(props.listing.content.markdown_content.itinerary).forEach(([key, value]) => {
        itineraryKeywords.push(key.toLowerCase());
        if (typeof value === 'string') {
          // Extract key terms from description
          const valueWords = value.toLowerCase().split(/\s+/);
          valueWords.forEach(word => {
            if (word.length > 4 && !['there', 'their', 'these', 'those', 'where', 'which', 'about'].includes(word)) {
              itineraryKeywords.push(word);
            }
          });
        }
      });
    }

    // Combine all relevant keywords
    const listingKeywords = [
      ...title.split(/\s+/),
      ...props.listing.content.meta_keywords || [],
      ...props.listing.content.featured_highlights || [],
      ...itineraryKeywords
    ].map(word => word.toLowerCase());

    // Process real reviews from the listing
    const processedReviews = props.listing.reviews
      .filter(review => {
        // Only show positive reviews
        if (review.reviewRating < 4) return false;

        // Check relevance based on content matching keywords
        const reviewContent = (review.reviewContent || '').toLowerCase();
        const reviewTitle = (review.reviewTitle || '').toLowerCase();

        // Calculate relevance score
        let relevanceScore = 0;

        // Check for exact listing code mention (strongest indicator)
        if (reviewContent.includes(props.listing.code.toLowerCase()) ||
          reviewTitle.includes(props.listing.code.toLowerCase())) {
          relevanceScore += 10;
        }

        // Check for title matches
        if (title.split(/\s+/).some(word => reviewContent.includes(word) || reviewTitle.includes(word))) {
          relevanceScore += 5;
        }

        // Check for itinerary keyword matches
        const matchedKeywords = listingKeywords.filter(keyword =>
          reviewContent.includes(keyword) || reviewTitle.includes(keyword)
        );

        relevanceScore += matchedKeywords.length;

        // Check for specific location mentions
        if (props.listing.origin && reviewContent.includes(props.listing.origin.toLowerCase())) {
          relevanceScore += 3;
        }
        if (props.listing.destination && reviewContent.includes(props.listing.destination.toLowerCase())) {
          relevanceScore += 3;
        }

        // Return true if review is relevant enough
        return relevanceScore > 2;
      })
      .map((review, index) => {
        // Determine gender based on name (simplified version)
        const authorName = review.author || '';
        const gender = authorName.length % 2 === 0 ? 'men' : 'women'; // Simple random assignment
        const avatarId = Math.floor(Math.random() * 70) + 1;

        // Country information with flags - randomly assigned if not present

        const countryInfo = countries.value[Math.floor(Math.random() * countries.value.length)];
        const location = review.nationality || countryInfo.countryName;

        return {
          id: index,
          name: authorName || 'Anonymous Guest',
          rating: review.reviewRating || 5,
          title: review.reviewTitle || 'Great Experience',
          text: review.reviewContent || 'We had a wonderful experience!',
          avatar: review.avatar || `https://randomuser.me/api/portraits/${gender}/${avatarId}.jpg`,
          location: location,
          countryFlag: review.nationality ? '' : countryInfo.flag // Only use flag if location is randomly assigned
        };
      })
      .slice(0, 6); // Limit to 6 reviews

    // Shuffle the reviews
    reviews.value = processedReviews.sort(() => Math.random() - 0.5);
  } else {
    // Use default reviews when no listing reviews available, but customize them to match the listing
    const defaultReviews = [
      {
        name: "Sarah Johnson",
        rating: 5,
        title: "Amazing Experience",
        text: "This tour exceeded all our expectations! The guides were friendly and knowledgeable. Highly recommend!",
        avatar: "https://randomuser.me/api/portraits/women/41.jpg",
        location: "United Kingdom",
        countryFlag: "🇬🇧"
      },
      {
        name: "Michael Liu",
        rating: 5,
        title: "Worth Every Penny",
        text: "Perfectly organized tour with great attention to details. We loved every moment of it.",
        avatar: "https://randomuser.me/api/portraits/men/32.jpg",
        location: "Australia",
        countryFlag: "🇦🇺"
      },
      {
        name: "Elena Rodriguez",
        rating: 4.5,
        title: "Unforgettable Day",
        text: "Beautiful locations and excellent service. The lunch was delicious and the guides were very attentive.",
        avatar: "https://randomuser.me/api/portraits/women/63.jpg",
        location: "Spain",
        countryFlag: "🇪🇸"
      },
      {
        name: "James Wilson",
        rating: 5,
        title: "Excellent Tour",
        text: "One of the best tours we've ever taken. Everything was well organized and the sights were breathtaking.",
        avatar: "https://randomuser.me/api/portraits/men/54.jpg",
        location: "Canada",
        countryFlag: "🇨🇦"
      },
      {
        name: "Sophie Kim",
        rating: 5,
        title: "Highlight of Our Trip",
        text: "This tour was definitely the highlight of our vacation. The scenery was incredible and our guide was fantastic.",
        avatar: "https://randomuser.me/api/portraits/women/28.jpg",
        location: "South Korea",
        countryFlag: "🇰🇷"
      },
      {
        name: "David Miller",
        rating: 4.5,
        title: "Great Value",
        text: "We had an amazing time on this tour. The transportation was comfortable and the guide was very informative.",
        avatar: "https://randomuser.me/api/portraits/men/22.jpg",
        location: "United States",
        countryFlag: "🇺🇸"
      }
    ];

    // Customize default reviews to match the listing if possible
    if (props.listing && props.listing.title) {
      // Extract key details from listing to customize reviews
      const title = props.listing.title;
      const type = props.listing.listing_type;
      const origin = props.listing.origin;
      const destination = props.listing.destination;

      // Map listing types to appropriate terms
      const typeTerms = {
        'SHARED_TRANSFER': 'shared transfer',
        'PRIVATE_TRANSFER': 'private transfer',
        'PRIVATE_BOAT_TOUR': 'private boat tour',
        'JOINING_TOUR': 'tour',
        'PRIVATE_TRANSFER_WITH_TOUR': 'transfer and tour',
        'AIRPORT_TRANSFER': 'airport pickup'
      };

      const typeTerm = typeTerms[type] || 'tour';

      defaultReviews.forEach(review => {
        // Get key terms from itinerary if available
        let itineraryTerms = [];
        if (props.listing.content.markdown_content.itinerary) {
          itineraryTerms = Object.keys(props.listing.content.markdown_content.itinerary).slice(0, 2);
        }

        // Customize review text to include listing details
        if (type.includes('TRANSFER')) {
          review.text = review.text.replace("this tour", `this ${typeTerm} from ${origin} to ${destination}`);
        } else if (itineraryTerms.length > 0) {
          review.text = review.text.replace("this tour", `${title} with visits to ${itineraryTerms.join(' and ')}`);
        } else {
          review.text = review.text.replace("this tour", title);
        }
      });
    }

    // Use the customized reviews
    reviews.value = [...defaultReviews].sort(() => Math.random() - 0.5);
  }
};

// Load content with a defined delay to ensure stable initial rendering
const loadContent = () => {
  // Load content with a defined delay to ensure stable initial rendering
  setTimeout(() => {
    isLoading.value = false;
    initializeReviews(); // Initialize reviews after loading
  }, 500); // Reduced from variable time to fixed 500ms for consistency
};

// Scroll to top on mount and initialize dimensions
onMounted(() => {
  window.scrollTo(0, 0);

  // Update dimensions before content loads to prevent CLS
  updateLayoutDimensions();

  // Setup layout before content loads
  document.documentElement.style.setProperty('--footer-height', footerHeight.value);
  document.documentElement.style.setProperty('--carousel-height', carouselHeight.value);

  loadContent();

  // Add scroll event listener for reviews
  if (reviewsScrollWrapper.value) {
    reviewsScrollWrapper.value.addEventListener('scroll', () => {
      // Debounce the scroll event
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        updateScrollIndex();
      }, 100);
    });
  }
});

// Add an enhanced mobile layout check
const isMobileOnly = ref(window.innerWidth < 600);


// Add a window resize listener with debounce to prevent excessive recalculations
let resizeTimeout;
const updateMobileStatus = () => {
  clearTimeout(resizeTimeout);
  resizeTimeout = setTimeout(() => {
    updateLayoutDimensions();
  }, 100); // Debounce resize events
};

// Listen for window resize events
window.addEventListener('resize', updateMobileStatus);

// Clean up when component is unmounted
onUnmounted(() => {
  window.removeEventListener('resize', updateMobileStatus);
  clearTimeout(resizeTimeout);
});

// Share to social media function
const shareToSocial = (platform) => {
  const url = `https://www.diskubrepalawan.com${route.fullPath}`;
  const text = props.listing?.title || 'Check out this tour';

  let shareUrl = '';
  switch (platform) {
    case 'facebook':
      shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
      break;
    case 'twitter':
      shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`;
      break;
    case 'whatsapp':
      shareUrl = `https://api.whatsapp.com/send?text=${encodeURIComponent(text + ' ' + url)}`;
      break;
  }

  if (shareUrl) {
    window.open(shareUrl, '_blank', 'width=600,height=400');
    showToast.value = true;
    toastMessage.value = `Shared to ${platform.charAt(0).toUpperCase() + platform.slice(1)}`;
  }
};

// Copy to clipboard function
const copyToClipboard = async () => {
  try {
    const url = `https://www.diskubrepalawan.com${route.fullPath}`;
    await navigator.clipboard.writeText(url);
    hasCopied.value = true;
    showToast.value = true;
    toastMessage.value = 'URL copied to clipboard!';

    // Reset copy button after 3 seconds
    setTimeout(() => {
      hasCopied.value = false;
    }, 3000);
  } catch (err) {
    console.error('Failed to copy: ', err);
    showToast.value = true;
    toastMessage.value = 'Failed to copy URL';
  }
};

// Open the review dialog with the selected review
const openReviewDialog = (index) => {
  currentReviewIndex.value = index;
  reviewDialog.value = true;
};

// Navigate to the next review
const showNextReview = () => {
  if (currentReviewIndex.value < reviews.value.length - 1) {
    currentReviewIndex.value++;
  }
};

// Navigate to the previous review
const showPreviousReview = () => {
  if (currentReviewIndex.value > 0) {
    currentReviewIndex.value--;
  }
};

// Format a random date for the review (within the last 3 months)
const formatReviewDate = () => {
  const today = new Date();
  const randomDaysAgo = Math.floor(Math.random() * 90); // Random day in the last 3 months
  const reviewDate = new Date(today);
  reviewDate.setDate(today.getDate() - randomDaysAgo);

  return reviewDate.toLocaleDateString(locale.value, {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

// Scroll reviews horizontally
const scrollReviews = (direction) => {
  if (!reviewsScrollWrapper.value) return;

  const container = reviewsScrollWrapper.value;
  const scrollAmount = 300; // Approximate width of a card + margin

  if (direction === 'next') {
    container.scrollBy({ left: scrollAmount, behavior: 'smooth' });
  } else {
    container.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
  }

  // Update the current index after scrolling
  setTimeout(() => {
    updateScrollIndex();
  }, 500);
};

// Update the current scroll index based on scroll position
const updateScrollIndex = () => {
  if (!reviewsScrollWrapper.value) return;

  const container = reviewsScrollWrapper.value;
  const scrollAmount = 300; // Approximate width of a card + margin
  currentScrollIndex.value = Math.round(container.scrollLeft / scrollAmount);
};

// Scroll to a specific index
const scrollToIndex = (index) => {
  if (!reviewsScrollWrapper.value) return;

  const container = reviewsScrollWrapper.value;
  const scrollAmount = 300; // Approximate width of a card + margin
  container.scrollTo({ left: index * scrollAmount, behavior: 'smooth' });
  currentScrollIndex.value = index;
};
</script>

<style scoped>
/* CSS variables for dynamic dimensions to prevent CLS */
:root {
  --footer-height: 70px;
  --carousel-height: 400px;
}

.content-container {
  position: relative;
  margin-bottom: 0;
  /* Removed variable margin - using fixed footer-spacer instead */
  max-width: 1200px;
  margin-left: auto;
  margin-right: auto;
  overflow-x: hidden;
  /* Prevent horizontal scrolling */
  width: 100%;
  /* Ensure container doesn't exceed viewport width */
  box-sizing: border-box;
  /* Include padding in width calculation */
}

/* Add better containment for all potential overflowing elements */
body {
  overflow-x: hidden;
  /* Prevent horizontal scrolling at the body level */
}

/* Fixed-height space for skeleton loader to prevent CLS */
.skeleton-loader {
  min-height: 500px;
  display: block;
  width: 100%;
}

/* Fixed space for carousel to prevent CLS */
.carousel-container {
  position: relative;
  width: 100%;
  max-width: 100%;
  overflow: visible;
  margin-bottom: 6rem;
  background-color: #f5f5f5;
  /* Placeholder color while loading */
  padding: 0;
}

.listing-carousel {
  border-radius: 0;
  overflow: visible !important;
  box-shadow: none;
  height: 100%;
  width: 100%;
}

/* Ensure rows don't cause overflow */
.m-0 {
  margin: 0 !important;
  max-width: 100%;
  box-sizing: border-box;
}

/* Prevent content from spilling outside containers */
.listing-section {
  position: relative;
  margin-bottom: 1.5rem;
  border-radius: 8px;
  transition: all 0.3s ease;
  width: 100%;
  box-sizing: border-box;
  overflow: hidden;
  /* Prevent content from spilling out */
}

/* Fixed height placeholder for suggestions to prevent CLS */
.suggestions-placeholder {
  min-height: 200px;
  width: 100%;
}

/* Ensure skeleton loader for suggestions matches final content dimensions */
.suggestions-skeleton {
  min-height: 200px;
}

/* Ensure share section is properly contained */
.share-section {
  background-color: white;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  padding: 24px;
  margin: 16px 0;
  width: 100%;
  border: 1px solid rgba(0, 0, 0, 0.08);
  box-sizing: border-box;
  overflow: hidden;
  /* Prevent content from spilling out */
}

.listing-main-content {
  margin-bottom: 0.5rem;
}

/* Reserve space for footer to prevent layout shift */
.footer-spacer {
  height: var(--footer-height);
  width: 100%;
  visibility: hidden;
}

/* Section styling for unified look */
.listing-section:not(:last-child)::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 16px;
  right: 16px;
  height: 1px;
  background-color: rgba(0, 0, 0, 0.08);
}

/* Remove card border for FAQ accordions */
:deep(.v-expansion-panel) {
  box-shadow: none !important;
  border: none !important;
  background-color: transparent !important;
}

:deep(.v-expansion-panel-title) {
  padding-left: 0 !important;
  padding-right: 0 !important;
}

:deep(.v-expansion-panel-text__wrapper) {
  padding-left: 0 !important;
  padding-right: 0 !important;
}

/* Consistent typography styling */
:deep(.text-h5) {
  font-size: 1.25rem !important;
  margin-bottom: 1rem !important;
  font-weight: bold !important;
}

:deep(.text-h6) {
  font-size: 1.1rem !important;
  margin-bottom: 0.75rem !important;
  font-weight: 500 !important;
}

/* Consistent section spacing */
:deep(section),
:deep(.section) {
  margin-bottom: 2rem;
}

:deep(p) {
  line-height: 1.6;
  margin-bottom: 0.75rem;
}

/* Remove alert borders */
:deep(.v-alert) {
  border: none;
}

/* Fix for footer alignment */
.footer-wrapper {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  width: 100%;
  z-index: 999;
  background-color: white;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.1);
  display: flex;
  justify-content: center;
  align-items: center;
  height: var(--footer-height);
  /* Use variable to match spacer */
}

:deep(.listing-footer) {
  width: 100%;
  max-width: 500px;
  /* Reduced from 1200px to 500px for larger screens */
  margin: 0 auto;
}

@media (max-width: 600px) {
  .content-container {
    padding: 0 0 80px 0 !important;
    /* Keep horizontal padding at 0, but ensure bottom padding for footer */
    margin-bottom: 0;
    /* Use padding instead of margin for bottom spacing */
  }

  .listing-section {
    padding-left: 16px;
    padding-right: 16px;
    margin-bottom: 1rem;
  }

  /* Fixed heights for mobile view to prevent CLS */
  .carousel-container {
    max-height: none;
    margin-bottom: 5rem;
  }

  .skeleton-loader {
    min-height: 300px;
  }

  /* Add more spacing between sections for better readability */
  .listing-section>div.mb-4 {
    margin-bottom: 24px !important;
    padding-bottom: 8px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  }

  /* Add more spacing around key elements */
  .listing-main-content {
    padding: 0 8px;
  }

  /* Add spacing to main content sections */
  :deep(.v-list) {
    padding: 8px 0 !important;
  }

  /* Add more spacing between sections */
  :deep(.mb-4) {
    margin-bottom: 16px !important;
  }

  :deep(.mb-3) {
    margin-bottom: 12px !important;
  }

  /* Better spacing for buttons and interactive elements */
  :deep(.v-btn) {
    margin: 4px !important;
  }

  /* Better padding for text content */
  :deep(p),
  :deep(.text-body-1),
  :deep(.text-body-2) {
    padding: 0 4px;
    margin-bottom: 12px !important;
  }

  :deep(.text-h5) {
    font-size: 1.15rem !important;
    padding: 8px 0 !important;
  }

  :deep(.text-h6) {
    font-size: 1rem !important;
    padding: 4px 0 !important;
  }

  /* Optimize list layout for small screens */
  :deep(.v-list-item) {
    padding: 10px 4px !important;
    min-height: auto !important;
  }

  /* Add padding to list items */
  :deep(.v-list-item-title) {
    padding: 2px 0;
  }

  /* Optimize chip groups for small screens */
  :deep(.v-chip-group) {
    flex-wrap: wrap;
    padding: 4px 0;
  }

  :deep(.v-chip) {
    margin: 0 4px 6px 0 !important;
  }

  /* Reduce section margins but increase padding */
  :deep(section),
  :deep(.section) {
    margin-bottom: 1rem;
    padding: 0 6px;
  }

  /* Make ListingSection more compact but with better internal spacing */
  .listing-section {
    margin-bottom: 16px;
    padding-top: 8px;
    padding-bottom: 12px;
  }

  /* Reduce carousel height */
  :deep(.listing-carousel) {
    max-height: none;
    margin-bottom: 0 !important;
  }

  /* Make headlines more compact */
  :deep(h2.text-h5) {
    margin-top: 12px;
    margin-bottom: 8px !important;
  }

  /* Compress list items */
  :deep(.v-list-item-title),
  :deep(.v-list-item-subtitle) {
    white-space: normal !important;
    overflow: visible !important;
    text-overflow: clip !important;
    line-height: 1.4 !important;
    /* Slightly increased for better readability */
    padding: 1px 0;
  }

  :deep(.listing-footer) {
    max-width: 100%;
    /* Full width on mobile */
    padding-left: 8px;
    padding-right: 8px;
  }

  /* Fixed footer height for mobile */
  .footer-wrapper {
    height: 80px;
  }

  .footer-spacer {
    height: 80px;
  }
}

/* Fix for medium-sized screens */
@media (min-width: 601px) and (max-width: 960px) {
  .content-container {
    padding-left: 12px;
    padding-right: 12px;
  }

  /* Moderate compression for medium screens */
  :deep(.listing-section) {
    padding: 0 16px;
  }
}

.share-buttons {
  display: flex;
  gap: 16px;
  margin-top: 24px;
  /* Increased from 16px to 24px */
  flex-wrap: wrap;
  padding-top: 8px;
  /* Added padding to the top */
}

.share-btn {
  padding: 12px 20px !important;
  height: auto !important;
  margin-right: 8px !important;
  margin-bottom: 8px !important;
  border-width: 2px !important;
  transition: transform 0.2s, box-shadow 0.2s;
}

.share-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
}

/* Review card styles */
.review-card {
  transition: transform 0.3s ease;
  border-radius: 12px;
  position: relative;
  border: 1px solid rgba(0, 0, 0, 0.08);
  cursor: pointer;
}

.review-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.1);
}

.review-text {
  font-style: italic;
  line-height: 1.4;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
}

/* Horizontal scrolling reviews for mobile */
.reviews-scroll-container {
  position: relative;
  width: 100%;
  overflow: hidden;
  padding: 8px 0;
  margin-bottom: 24px;
}

.reviews-scroll-inner {
  position: relative;
  display: flex;
  align-items: center;
}

.reviews-scroll-wrapper {
  display: flex;
  overflow-x: auto;
  overflow-y: hidden;
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  /* Firefox */
  padding-bottom: 16px;
  /* Space for scrollbar */
  padding-left: 4px;
  margin-right: -16px;
  flex: 1;
}

.reviews-scroll-wrapper::-webkit-scrollbar {
  display: none;
  /* Chrome, Safari and Opera */
}

.review-card-scroll {
  flex: 0 0 auto;
  scroll-snap-align: start;
  height: auto;
  display: flex;
  flex-direction: column;
  margin-bottom: 8px;
}

.scroll-indicator {
  flex: 0 0 120px;
  height: 100%;
  min-height: 220px;
  border-radius: 12px;
  background-color: rgba(0, 0, 0, 0.03);
  margin-right: 24px;
}

.reviews-nav-button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background-color: white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  z-index: 1;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.reviews-nav-button:hover {
  background-color: #f5f5f5;
  transform: scale(1.05);
}

.reviews-nav-prev {
  margin-right: 8px;
}

.reviews-nav-next {
  margin-left: 8px;
}

.reviews-indicators {
  display: flex;
  justify-content: center;
  margin-top: 16px;
}

.reviews-indicator-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: #e0e0e0;
  margin: 0 4px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.reviews-indicator-dot.active {
  background-color: #1976d2;
  transform: scale(1.2);
}

/* Mobile adjustments for share buttons */
@media (max-width: 600px) {
  .share-section {
    padding: 12px;
    margin: 8px 0;
  }

  .share-section h2 {
    font-size: 1rem !important;
    margin-bottom: 8px !important;
  }

  .share-buttons {
    gap: 8px;
    margin-top: 12px;
    justify-content: space-between;
    flex-wrap: nowrap;
    padding-top: 0;
  }

  .share-btn {
    flex: 0 0 auto;
    max-width: 22%;
    min-width: unset;
    padding: 6px !important;
    margin: 0 !important;
    height: 40px !important;
    width: 40px !important;
    border-width: 1px !important;
  }

  .share-btn .v-icon {
    margin-right: 0;
  }
}

/* Add CSS for review dialog */
.review-dialog-card {
  border-radius: 12px;
  overflow: hidden;
}

.review-navigation {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px 8px;
}

.review-dialog-text {
  line-height: 1.6;
  font-style: italic;
}

.review-content-wrapper {
  min-height: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px 0;
  touch-action: pan-y;
}

.keyboard-instructions {
  color: rgba(0, 0, 0, 0.6);
}

.keyboard-instructions kbd {
  background-color: #f5f5f5;
  border: 1px solid #ddd;
  border-radius: 3px;
  box-shadow: 0 1px 1px rgba(0, 0, 0, 0.2);
  font-size: 0.75rem;
  padding: 2px 5px;
}

.review-nav-btn {
  min-width: 40px;
  transition: all 0.3s ease;
}

.review-nav-btn:hover:not(:disabled) {
  transform: scale(1.05);
}

/* Mobile responsive styles for review dialog */
@media (max-width: 600px) {
  .review-navigation {
    padding: 12px;
  }

  .review-nav-btn {
    min-width: 36px;
  }
}
</style>
